import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import ComplianceFooter from "@/components/ComplianceFooter";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy - Ascendry";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
        <h1 className="text-4xl font-bold mb-8 text-center">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-muted-foreground">
            <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
          </p>

          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
            <p>
              We collect information you provide directly to us, such as when you create an account, 
              book mentoring sessions, or contact us for support. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Personal information (name, email address, phone number)</li>
              <li>Profile information and preferences</li>
              <li>Payment and billing information (processed securely by Stripe)</li>
              <li>Communications with mentors and support team</li>
              <li>Usage data and analytics</li>
              <li>Device information and technical data</li>
            </ul>
            <p className="mt-4">
              <strong>Legal Basis for Processing (GDPR):</strong> We process your data based on:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contract performance:</strong> To provide our mentoring services</li>
              <li><strong>Legitimate interests:</strong> To improve our platform and prevent fraud</li>
              <li><strong>Consent:</strong> For marketing communications and optional features</li>
              <li><strong>Legal obligations:</strong> To comply with tax and regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and improve our mentoring services</li>
              <li>Process payments and manage your account</li>
              <li>Facilitate communication between students and mentors</li>
              <li>Send important updates and notifications</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Provide customer support and technical assistance</li>
              <li>Comply with legal obligations and regulatory requirements</li>
            </ul>
            
            <div className="bg-muted p-4 rounded-lg mt-6">
              <h3 className="font-semibold mb-2">Data Retention Policies:</h3>
              <ul className="space-y-1 text-sm">
                <li><strong>Account data:</strong> Retained while your account is active</li>
                <li><strong>Session data:</strong> Retained for 7 years for tax and legal compliance</li>
                <li><strong>Payment data:</strong> Retained per Stripe's data retention policy</li>
                <li><strong>Support communications:</strong> Retained for 3 years</li>
                <li><strong>Usage analytics:</strong> Anonymized after 2 years</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Information Sharing and International Transfers</h2>
            <p>
              We do not sell or rent your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Mentors when you book sessions (limited to necessary information)</li>
              <li>Service providers who assist in platform operations</li>
              <li>Legal authorities when required by law</li>
              <li>Business partners for joint services (with your consent)</li>
            </ul>
            
            <div className="bg-muted p-4 rounded-lg mt-6">
              <h3 className="font-semibold mb-2">Data Processors & International Transfers:</h3>
              <p className="text-sm mb-2">
                We work with trusted service providers who may process your data. Some of these providers 
                are located outside the EU/EEA:
              </p>
              <ul className="space-y-1 text-sm">
                <li><strong>Supabase (USA):</strong> Database hosting - covered by adequate safeguards</li>
                <li><strong>Stripe (USA):</strong> Payment processing - Privacy Shield certified</li>
                
                <li><strong>AWS (Global):</strong> Cloud infrastructure - Privacy Shield certified</li>
              </ul>
              <p className="text-sm mt-2">
                All transfers are protected by appropriate safeguards including Standard Contractual Clauses 
                and adequacy decisions.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Session Platforms</h2>
            <p>
              Mentors use various communication platforms (Zoom, Google Meet, Discord, etc.) for sessions. 
              We do not store or process data from these third-party platforms directly. Your use of 
              these platforms is subject to their respective privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Your Rights Under GDPR</h2>
            <p>If you are in the EU/EEA, you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right of Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate information</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Right to Data Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how we process your data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for marketing or optional features</li>
            </ul>
            
            <div className="bg-primary/10 p-4 rounded-lg mt-4">
              <h3 className="font-semibold mb-2">How to Exercise Your Rights:</h3>
              <p className="text-sm">
                Contact us at{" "}
                <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                  lance@blaqout.us
                </a>{" "}
                with your request. We will respond within 30 days. You also have the right to lodge a 
                complaint with your local data protection authority.
              </p>
            </div>
            
            <p className="mt-4">
              <strong>Data Protection Officer:</strong> For privacy-related inquiries, contact our DPO at{" "}
              <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                lance@blaqout.us
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Data Security and Breach Notification</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your 
              information against unauthorized access, alteration, disclosure, or destruction, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication protocols</li>
              <li>Secure development practices</li>
              <li>Regular backups and disaster recovery procedures</li>
            </ul>
            
            <div className="bg-muted p-4 rounded-lg mt-4">
              <h3 className="font-semibold mb-2">Data Breach Notification:</h3>
              <p className="text-sm">
                In the unlikely event of a data breach that poses a high risk to your rights and freedoms, 
                we will notify you within 72 hours of becoming aware of the breach. We will inform you of 
                the nature of the breach, likely consequences, and measures taken to address it.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to provide and improve our services. 
              For detailed information about our cookie usage, please see our{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Cookie Policy
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new policy on this page and updating the effective date. 
              For significant changes, we may also send you an email notification.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at{" "}
              <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                lance@blaqout.us
              </a>
            </p>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <h3 className="font-semibold mb-2">Data Controller Information:</h3>
              <p className="text-sm">
                <strong>Ascendry</strong><br />
                Email: lance@blaqout.us<br />
                For EU residents: Our EU representative can be contacted at the same email address.
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default PrivacyPolicy;